/**
 * Auth Context - Enterprise Authentication State Management
 * Provides authentication state and methods throughout the application
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import authService, {
  AuthUser,
  LoginCredentials as ServiceLoginCredentials,
  FieldUserLoginCredentials,
} from '../services/auth.service';
import authorizationService from '../services/authorization.service';
import logger from '../utils/logger';
import { showToast } from '../components/common/CustomToast';
import { startTokenRefreshScheduler, stopTokenRefreshScheduler } from '../services/apiClient';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/** Login credentials type - generic for both corporate and field users */
interface LoginCredentials {
  username?: string;
  email?: string;
  mobile?: string;
  password?: string;
  otp?: string;
  organizationId?: number;
  device_info?: {
    browser: string;
    browserVersion: string;
    os: string;
    osVersion: string;
    deviceType?: 'desktop' | 'mobile' | 'tablet';
    screenResolution?: string;
  };
}

/** Login response type */
interface LoginResult {
  success: boolean;
  otpRequired?: boolean;
  data?: {
    user?: AuthUser;
    mobile?: string;
    [key: string]: unknown;
  };
}

interface AuthContextType {
  // State
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  initializing: boolean;

  // Methods
  login: (credentials: LoginCredentials) => Promise<LoginResult>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateUser: (userData: Partial<AuthUser>) => void;
  checkAuth: () => boolean;
}

// ============================================================================
// CONTEXT CREATION
// ============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// AUTH PROVIDER COMPONENT
// ============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(() => {
    if (authService.isAuthenticated()) {
      return authService.getCurrentUser();
    }
    return null;
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [initializing, setInitializing] = useState<boolean>(true);
  const hasInitialized = useRef(false);

  /**
   * Initialize authentication on mount (runs once)
   */
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Initialize authentication - check if user is already logged in
   */
  const initializeAuth = async () => {
    try {
      logger.info('Initializing authentication...');

      if (authService.isAuthenticated()) {
        const currentUser = authService.getCurrentUser();

        if (currentUser) {
          logger.info('User authenticated', { userId: currentUser.id });

          startTokenRefreshScheduler();

          authorizationService.getAssignedModules().catch(err => {
            logger.warn('Failed to pre-load permissions', err);
          });
        } else {
          logger.warn('Token exists but user data not found');
          await handleLogout();
        }
      } else {
        logger.info('No active session found');
      }
    } catch (error) {
      logger.error('Auth initialization failed', error);
      await handleLogout();
    } finally {
      setInitializing(false);
    }
  };

  /**
   * Login method - supports both corporate and field user login
   */
  const login = async (credentials: LoginCredentials): Promise<LoginResult> => {
    setLoading(true);

    try {
      logger.info('Login attempt', { username: credentials.username || credentials.mobile });

      let response;

      // Determine login type based on credentials
      if (credentials.mobile && credentials.otp) {
        // Field user login - cast to expected type since we validated mobile exists
        response = await authService.fieldUserLogin(
          credentials as Omit<FieldUserLoginCredentials, 'device_info'>
        );
      } else {
        // Corporate login - cast to expected type
        response = await authService.corporateLogin(
          credentials as Omit<ServiceLoginCredentials, 'device_info'>
        );
      }

      // Check if OTP is required
      if (response.success && 'mobile' in response.data) {
        // OTP required
        logger.info('OTP required for login');
        return {
          success: false,
          otpRequired: true,
          data: response.data,
        };
      }

      // Login successful
      if (response.success && 'user' in response.data) {
        setUser(response.data.user);
        logger.info('Login successful', { userId: response.data.user.id });

        // Start proactive token refresh scheduler
        startTokenRefreshScheduler();

        // Pre-load user permissions in background
        authorizationService.getAssignedModules().catch(err => {
          logger.warn('Failed to load permissions after login', err);
        });

        showToast.success('Login successful!');

        return {
          success: true,
          data: response.data,
        };
      }

      throw new Error('Invalid login response');
    } catch (error: unknown) {
      logger.error('Login failed', error);

      const errorMessage = error instanceof Error ? error.message : 'Login failed. Please try again.';
      showToast.error(errorMessage);

      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout method
   */
  const logout = async (): Promise<void> => {
    await handleLogout();
  };

  /**
   * Internal logout handler
   */
  const handleLogout = async (): Promise<void> => {
    setLoading(true);

    try {
      logger.info('Logging out user', { userId: user?.id });

      // Stop proactive token refresh scheduler
      stopTokenRefreshScheduler();

      await authService.logout();
      authorizationService.clearCache();

      setUser(null);

      logger.info('Logout successful');
      showToast.info('Logged out successfully');
    } catch (error: unknown) {
      logger.error('Logout error', error);
      // Clear local state even if API call fails
      stopTokenRefreshScheduler();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refresh user data from storage
   */
  const refreshUser = async (): Promise<void> => {
    try {
      const currentUser = authService.getCurrentUser();
      setUser(currentUser);
      logger.debug('User data refreshed');
    } catch (error) {
      logger.error('Failed to refresh user', error);
    }
  };

  /**
   * Update user data (for profile updates)
   */
  const updateUser = useCallback((userData: Partial<AuthUser>) => {
    setUser(prevUser => {
      if (!prevUser) return null;

      const updatedUser = {
        ...prevUser,
        ...userData,
      };

      // Persist updated user to secure storage so refreshUser() picks it up
      try {
        authService.updateStoredUser(updatedUser);
      } catch (error) {
        logger.error('Failed to update user in storage', error);
      }

      return updatedUser;
    });

    logger.debug('User data updated');
  }, []);

  /**
   * Check if user is authenticated (synchronous check)
   */
  const checkAuth = useCallback((): boolean => {
    return authService.isAuthenticated() && user !== null;
  }, [user]);

  // Context value
  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    initializing,
    login,
    logout,
    refreshUser,
    updateUser,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ============================================================================
// CUSTOM HOOK
// ============================================================================

/**
 * useAuth Hook - Access authentication context
 * @throws Error if used outside AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
};

// ============================================================================
// EXPORTS
// ============================================================================

export default AuthContext;
